import { useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth/context";
import { Splash } from "@/components/shell/Splash";
import { EmailVerifiedGate } from "./EmailVerifiedGate";
import { markActive } from "@/lib/db/members";

type Props = {
  children: ReactNode;
  requireRole?: "parent" | "child";
};

export function AuthGuard({ children, requireRole }: Props) {
  const { loading, firebaseUser, userDoc } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!firebaseUser) {
      navigate("/login", { replace: true });
      return;
    }
    if (firebaseUser && !userDoc) {
      navigate("/signup?step=household", { replace: true });
      return;
    }
    if (requireRole && userDoc && userDoc.role !== requireRole) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, firebaseUser, userDoc, requireRole, navigate]);

  // Heartbeat aktywności — pisze members/{uid}.lastActiveAt z throttle 5 min
  // in-memory (markActive). Jedyne źródło DAU/WAU/MAU dla panelu admin; bez
  // tego „aktywny" znaczyłoby tylko „zgłosił/zatwierdził coś", co pomija
  // userów którzy tylko otwierają apkę żeby coś sprawdzić.
  useEffect(() => {
    if (!firebaseUser || !userDoc) return;
    void markActive(userDoc.householdId, firebaseUser.uid);
  }, [firebaseUser, userDoc]);

  if (loading) {
    return <Splash />;
  }
  if (!firebaseUser || !userDoc) return null;
  if (requireRole && userDoc.role !== requireRole) return null;

  return <EmailVerifiedGate>{children}</EmailVerifiedGate>;
}
